/**
 * World 10 — "The First Shore" (All, levels 91–100). ACT II, chapter 5 — THE GRAND
 * FINALE: the chase ends where the island began; the First Keeper is given back
 * his name and the Soul-Eater's hunger ends. Levels/caps stamped from World 10's
 * band (91→100) — its finale boss is authored at level 100, the top of the campaign's
 * bands, and like every other fighter in every mode it is FOUGHT at COMBAT_MAX_LEVEL.
 * Squads are mixed-element so the world's `element` is 'All'. Finale boss (slot 0)
 * is Trixon — the First Guardian of all, the leviathan that rose from the sea at
 * the First Shore. The featured mini-boss is Pikoto, "The First Friend" — the
 * tiny soul the First Keeper loved before he was a Keeper at all. These are the
 * last two of the campaign's 19 featured souls.
 */

import {
  node,
  giftNode,
  giftTracks,
  giftXp,
  rival,
  CAMPAIGN_XP_NORMAL,
  CAMPAIGN_XP_MINI_BOSS,
  CAMPAIGN_XP_ZONE_BOSS,
  GORLY,
  FERNAIL,
  DRAGO,
  TORO,
  ZARICAT,
  YETI,
  SUIMON,
  AQUA,
  RAY,
  SUMIYA,
  PINKY,
  HOTPAW,
  PIKOTO,
  TRIXON,
  TALION,
  PEFAREON,
  type CampaignWorld,
} from './builders.js';
import { GIFT_SKILL_SUIKA } from '../cardShopConfig.js';
import {
  WORLD10_NODE_STORIES as S,
  WORLD10_ZONE_STORIES as Z,
  WORLD10_TAGLINE,
} from './stories/world10.js';

export const WORLD_10: CampaignWorld = {
  worldId: 'w10',
  name: 'First Shore',
  element: 'All',
  featuredSpeciesId: TRIXON,
  story: WORLD10_TAGLINE,
  locked: true,
  zones: [
    {
      zoneId: 'z27',
      name: 'Origin Sands',
      element: 'All',
      story: Z['z27'],
      nodes: [
        node({
          nodeId: 'z27-n1',
          kind: 'normal',
          rivalName: 'First Trainer',
          rivalTitle: 'Old Echo',
          ...S['z27-n1'],
          squad: [rival(GORLY, 'Origin'), rival(RAY, 'Dawnbramble'), rival(SUMIYA, 'Firstbloom')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: GORLY,
        }),
        giftNode({
          nodeId: 'z27-g1',
          giver: 'First Guide',
          label: 'Trailside Gift',
          story: 'A guide greets you on the first sands.',
          line: 'Welcome to the first shore! Take these Soul Shards!',
          reward: { tracks: giftTracks(TRIXON) },
          portraitSpeciesId: TRIXON,
        }),
        node({
          nodeId: 'z27-n2',
          kind: 'normal',
          // Clan Z's last ambush (docs/campaign/VILLAIN_ESCALATION.md E16): they never turn good,
          // and Dash and Shade come at their strongest, never weaker on a return (SB17).
          rivalName: 'Zip',
          rivalTitle: 'Clan Z',
          ...S['z27-n2'],
          squad: [rival(TALION, 'Dash'), rival(PEFAREON, 'Shade'), rival(FERNAIL, 'Firstwater')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: TALION,
        }),
        node({
          nodeId: 'z27-n3',
          kind: 'normal',
          rivalName: 'First Guard',
          rivalTitle: 'First Guard',
          ...S['z27-n3'],
          squad: [rival(PIKOTO, 'Firstflame'), rival(DRAGO, 'Relic'), rival(HOTPAW, 'Lantern')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: PIKOTO,
        }),
        node({
          nodeId: 'z27-n4',
          kind: 'zone_boss',
          rivalName: 'First Gate',
          rivalTitle: 'First Gate',
          ...S['z27-n4'],
          squad: [rival(TRIXON, 'Genesis'), rival(TORO, 'Outset'), rival(ZARICAT, 'Onset')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_ZONE_BOSS,
          portraitSpeciesId: TRIXON,
        }),
      ],
    },
    {
      zoneId: 'z28',
      name: 'Year Tides',
      element: 'All',
      story: Z['z28'],
      nodes: [
        node({
          nodeId: 'z28-n1',
          kind: 'normal',
          rivalName: 'First Bond',
          rivalTitle: 'First Bond',
          ...S['z28-n1'],
          squad: [rival(GORLY, 'Tender'), rival(PINKY, 'Trust'), rival(SUMIYA, 'Warmth')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: GORLY,
        }),
        giftNode({
          nodeId: 'z28-g1',
          giver: 'Old Keeper',
          label: 'Training Charm',
          story: 'An old Keeper offers you a warm charm.',
          line: 'This charm carried the first Keepers. Now you.',
          reward: { xp: giftXp(9) },
          portraitSpeciesId: TRIXON,
        }),
        node({
          nodeId: 'z28-n2',
          kind: 'normal',
          rivalName: 'Lonely One',
          rivalTitle: 'So Alone',
          ...S['z28-n2'],
          squad: [rival(ZARICAT, 'Solitude'), rival(YETI, 'Hollow'), rival(SUIMON, 'Dusk')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: ZARICAT,
        }),
        node({
          nodeId: 'z28-n3',
          kind: 'normal',
          rivalName: 'Doubt',
          rivalTitle: "The Doubt",
          ...S['z28-n3'],
          squad: [rival(TRIXON, 'Maybe'), rival(TORO, 'Whatif'), rival(AQUA, 'Perhaps')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: TRIXON,
        }),
        node({
          nodeId: 'z28-n4',
          kind: 'zone_boss',
          rivalName: 'Tide of Years',
          rivalTitle: 'Lonely Years',
          ...S['z28-n4'],
          squad: [rival(SUIMON, 'Fathom'), rival(FERNAIL, 'Brine'), rival(YETI, 'Glacier')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_ZONE_BOSS,
          portraitSpeciesId: SUIMON,
        }),
      ],
    },
    {
      zoneId: 'z29',
      name: 'First Hearth',
      element: 'All',
      story: Z['z29'],
      nodes: [
        node({
          nodeId: 'z29-n1',
          kind: 'normal',
          rivalName: 'First Champ',
          rivalTitle: 'Whole At Last',
          ...S['z29-n1'],
          squad: [rival(SUMIYA, 'Truename'), rival(DRAGO, 'Hearth'), rival(GORLY, 'Faithful')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: SUMIYA,
        }),
        giftNode({
          nodeId: 'z29-g1',
          giver: 'Shrine Keeper',
          label: 'Soul Token',
          story: 'By the tide, you find the first shrine.',
          line: 'The first shore leaves a big handful of Soul Shards here. Take them!',
          reward: { tracks: giftTracks(TRIXON, true), suika: GIFT_SKILL_SUIKA },
          portraitSpeciesId: TRIXON,
        }),
        node({
          nodeId: 'z29-n2',
          kind: 'normal',
          rivalName: 'Soul-Eater',
          rivalTitle: 'Its Last Plea',
          ...S['z29-n2'],
          squad: [rival(ZARICAT, 'Surrender'), rival(YETI, 'Quiet'), rival(TRIXON, 'Rest')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: ZARICAT,
        }),
        node({
          nodeId: 'z29-n3',
          kind: 'mini_boss',
          rivalName: 'First Friend',
          rivalTitle: 'Last Soul',
          ...S['z29-n3'],
          squad: [rival(PIKOTO, 'First Friend'), rival(GORLY, 'Firstgrief'), rival(SUMIYA, 'Sorrow')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_MINI_BOSS,
          portraitSpeciesId: PIKOTO,
          soulSpeciesId: PIKOTO,
        }),
        node({
          nodeId: 'z29-n4',
          kind: 'zone_boss',
          rivalName: 'Firstheart',
          rivalTitle: 'First Guardian',
          ...S['z29-n4'],
          squad: [rival(TRIXON, 'Firstheart'), rival(FERNAIL, 'Firsttide'), rival(PIKOTO, 'Firstflame')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_ZONE_BOSS,
          portraitSpeciesId: TRIXON,
          soulSpeciesId: TRIXON,
        }),
      ],
    },
  ],
};
